<template>
  <div class="interaktive-grafik-edit">
    <div v-if="!initialDataLoaded">
      {{ translationHelper.getTranslation("ig_label_loading_data") }}
    </div>
    <template v-else>
      <div class="row">
        <div class="large-17 columns">
          <div class="row template-row">
            <div class="large-20 columns title-col">
              <label for="templateSelect">{{
                translationHelper.getTranslation("ig_title_template")
              }}</label>
            </div>
            <div class="large-20 columns themes-select-col">
              <v-select
                id="templateSelect"
                class="themes-select"
                :options="availableTemplates"
                label="name"
                :searchable="false"
                :clearable="false"
                :multiple="false"
                :value="currentTemplate"
                @input="setCurrentTemplate"
              >
                <template slot="option" slot-scope="option">
                  <div class="template-option">
                    <img :src="option.image.source" />
                    <span>{{ option.name }}</span>
                  </div>
                </template>
              </v-select>
            </div>
          </div>
        </div>
        <div
          class="large-3 columns template-img-col"
          :style="
            currentTemplate &&
            currentTemplate.image &&
            currentTemplate.image.source
              ? 'background-image:url(\'' + currentTemplate.image.source + '\')'
              : ''
          "
        >
          &nbsp;
        </div>
      </div>
      <div class="row template-detail-row">
        <div class="large-5 columns template-detail label">
          {{ translationHelper.getTranslation("ig_label_fachthemen_min") }}
        </div>
        <div class="large-1 columns template-detail value">
          {{ currentTemplate.minTopics }}
        </div>
        <div class="large-2 columns template-detail label">
          {{ translationHelper.getTranslation("ig_label_fachthemen_max") }}
        </div>
        <div class="large-1 columns template-detail value">
          {{ currentTemplate.maxTopics }}
        </div>
        <div class="large-1 columns template-detail label">
          {{ translationHelper.getTranslation("ig_label_template_type") }}
        </div>
        <div class="large-5 columns template-detail value">
          {{ currentTemplate.lineTypeName }}
        </div>
        <div class="large-3 columns template-detail label">
          {{
            translationHelper.getTranslation("ig_label_template_has_planets")
          }}
        </div>
        <div class="large-2 columns template-detail value">
          {{
            currentTemplate.hasPlanets
              ? translationHelper.getTranslation("ig_label_yes")
              : translationHelper.getTranslation("ig_label_no")
          }}
        </div>
      </div>
      <div class="row template-department-row">
        <div class="large-3 columns title-col">
          <label class="label">{{
            translationHelper.getTranslation("ig_label_department")
          }}</label>
        </div>
        <div class="large-17 columns depts-select-col">
          <v-select
            :options="availableDepartments"
            class="depts-select"
            v-model="selectedDepartment"
            :reduce="(dept) => dept.code"
            :searchable="false"
            :clearable="false"
            :multiple="false"
            @option:selected="saveDept"
          ></v-select>
        </div>
      </div>
      <div
        class="row department-icon-error-row"
        v-if="availableSpecialistTopicIcons.length === 0"
      >
        <div class="large-20 columns">
          <div class="error-message" v-html="noIconsErrorMessage"></div>
        </div>
      </div>
      <div class="row template-initial-offer-type-row">
        <div class="large-3 columns title-col">
          <label class="label">{{
            translationHelper.getTranslation("ig_label_initial_offer")
          }}</label>
        </div>
        <div class="large-17 columns init_offer-select-col">
          <v-select
            :options="availableOfferTypes"
            class="init_offer-select"
            v-model="selectedInitialOfferType"
            :reduce="(offer) => offer.id"
            code="id"
            label="name"
            :searchable="false"
            :clearable="false"
            :multiple="false"
            @option:selected="saveInitialOffer"
          ></v-select>
        </div>
      </div>
      <div class="row specialist-topic-section">
        <div class="large-12 columns title-col">
          <label class="label">{{
            translationHelper.getTranslation("ig_label_fachthemen")
          }}</label>
        </div>
        <div
          class="large-8 columns topic-actions-col"
          @click="newSpecialistTopicStart"
          v-if="availableSpecialistTopicIcons.length > 0"
        >
          <div class="add-new-wrapper">
            <i class="ig_icons white add"></i
            ><span>{{
              translationHelper.getTranslation("ig_button_addnew")
            }}</span>
          </div>
        </div>
        <div v-else class="large-8 columns topic-actions-col">
          <label class="label">&nbsp;</label>
        </div>
      </div>
      <div class="row">
        <div class="large-20 columns specialist-topic-col">
          <div v-if="showErrorMessage" class="error-message">
            {{ errorMessage }}
          </div>
          <div
            v-if="settings.availableSpecialistTopics.length === 0"
            class="no-topics"
          >
            {{
              translationHelper.getTranslation("ig_label_no_fachthemen_found")
            }}
          </div>
          <container
            @drop="onDrop"
            id="specialistTopicList"
            class="specialist-topic-list"
            lock-axis="y"
            drag-handle-selector=".topic-drag-handle"
            :animation-duration="1000"
          >
            <draggable
              v-for="(
                specialistTopic, index
              ) in settings.availableSpecialistTopics"
              :key="specialistTopic.id"
              class="specialist-topic-draggable"
            >
              <specialist-topic
                v-model="settings.availableSpecialistTopics[index]"
                :isQuerschnittsThema="
                  index == 0 &&
                  currentTemplate.lineTypeId ==
                    '469831c6-057e-46d6-8506-1f7991058721'
                "
                :canEdit="availableSpecialistTopicIcons.length > 0"
                :glass-item-data="glassItemData"
                :available-offer-types="availableOfferTypes"
                :available-specialist-topic-icons="
                  availableSpecialistTopicIcons
                "
                :translationHelper="translationHelper"
                @deleted="specialistTopicDeleted"
              />
            </draggable>
          </container>
        </div>
      </div>

      <template v-if="newSpecialistTopicOpen">
        <specialist-topic-edit
          v-model="selectedSpecialistTopic"
          :show-modal="newSpecialistTopicOpen"
          :available-specialist-topic-icons="availableSpecialistTopicIcons"
          :glass-item-data="glassItemData"
          :translationHelper="translationHelper"
          method="save"
          @close-window="newSpecialistTopicEnd"
          @saved="specialistTopicSaved"
        ></specialist-topic-edit>
      </template>
    </template>
  </div>
</template>
<script>
import { TranslationHelper } from "../../../_Base/Utils";
import InteraktiveGrafik from "../../../_Api/InteraktiveGrafik";
import { Container, Draggable } from "vue-smooth-dnd";
import { applyDrag } from "../../../_Base/Utils";
import specialistTopic from "./components/specialistTopic.vue";
import specialistTopicEdit from "./components/specialistTopicEdit.vue";

export default {
  components: {
    specialistTopic,
    specialistTopicEdit,
    Container,
    Draggable
  },
  data() {
    return {
      avoidDrop: false,
      glassItemData: null,
      errorMessage: "",
      translationHelper: {},
      initialDataLoaded: false,
      currentTemplateId: null,
      currentTemplate: null,
      availableOfferTypes: [],
      availableLineTypes: [],
      availableTemplates: [],
      availableDepartments: [],
      selectedDepartment: "i",
      selectedInitialOfferType: null,
      newSpecialistTopicOpen: false,
      selectedSpecialistTopic: {
        id: "",
        name: "",
        description: "",
        url: "",
        icon: { id: "", source: "", alt: "" }
      },
      settings: {
        availableSpecialistTopics: [],
      },
      availableSpecialistTopicIcons: []
    };
  },
  computed: {
    showErrorMessage() {
      if (
        this.settings.availableSpecialistTopics.length <
        this.currentTemplate.minTopics
      ) {
        this.errorMessage = this.translationHelper.getTranslation(
          "ig_error_min_fachthemen"
        );
        return true;
      }
      if (
        this.settings.availableSpecialistTopics.length >
        this.currentTemplate.maxTopics
      ) {
        this.errorMessage = this.translationHelper.getTranslation(
          "ig_error_max_fachthemen"
        );
        return true;
      }
      return false;
    },
    noIconsErrorMessage() {
      if (this.selectedDepartment) {
        var dptInfo = this.availableDepartments.find(
          (x) => x.code == this.selectedDepartment
        );
        if (dptInfo) {
          return this.translationHelper
            .getTranslation("ig_error_dept_no_fachthemen_icons")
            .replace("##DEPT##", dptInfo.label)
            .replace("##CODE##", dptInfo.code);
        }
      }
      return "";
    }
  },
  created() {
    this.glassItemData = this.$parent.$data.glassItemData;
    this.translationHelper = new TranslationHelper(
      this.$parent.$data.initialTranslations
    );
    this.loadInitialData();
  },
  methods: {
    onDrop(dropResult) {
      this.avoidDrop = true;
      var updateSpecialistTopicTasks = [];
      this.settings.availableSpecialistTopics = applyDrag(
        this.settings.availableSpecialistTopics,
        dropResult
      );
      for (
        let index = 0;
        index < this.settings.availableSpecialistTopics.length;
        index++
      ) {
        if (this.settings.availableSpecialistTopics[index].sortOrder != index) {
          this.settings.availableSpecialistTopics[index].sortOrder = index;
          updateSpecialistTopicTasks.push(
            this.updateSpecialistTopic(
              this.settings.availableSpecialistTopics[index]
            )
          );
        }
      }
      Promise.all(updateSpecialistTopicTasks).then(() => {
        this.avoidDrop = false;
      });
    },
    loadInitialData() {
      this.availableDepartments.push({
        code: "ta",
        label: this.translationHelper.getTranslation("ig_label_department_ta"),
      });
      this.availableDepartments.push({
        code: "w",
        label: this.translationHelper.getTranslation("ig_label_department_w"),
      });
      this.availableDepartments.push({
        code: "i",
        label: this.translationHelper.getTranslation("ig_label_department_i"),
      });
      this.availableDepartments.push({
        code: "sa",
        label: this.translationHelper.getTranslation("ig_label_department_sa"),
      });
      this.availableDepartments.push({
        code: "dk",
        label: this.translationHelper.getTranslation("ig_label_department_dk"),
      });
      this.availableDepartments.push({
        code: "m",
        label: this.translationHelper.getTranslation("ig_label_department_m"),
      });
      this.availableDepartments.push({
        code: "h",
        label: this.translationHelper.getTranslation("ig_label_department_h"),
      });
      let initialDataTasks = [];

      initialDataTasks.push(
        InteraktiveGrafik.getAllOfferTypes().then((response) =>
          this.fillAvailableOfferTypes(response)
        )
      );
      initialDataTasks.push(
        InteraktiveGrafik.getAllTemplates().then((response) =>
          this.fillAvailableTemplates(response)
        )
      );
      initialDataTasks.push(
        InteraktiveGrafik.getSpecialistTopics(this.glassItemData).then(
          (response) => this.fillAvailableSpecialistTopics(response)
        )
      );
      initialDataTasks.push(
        InteraktiveGrafik.getAllLineTypes().then((response) =>
          this.fillAvailableLineTypes(response)
        )
      );

      //Not alowed to use await in create hook
      Promise.all(initialDataTasks).then(() => {
        InteraktiveGrafik.getInteraktiveGrafikSettings(this.glassItemData).then(
          (responseSettings) => {
            this.updateCurrentTemplate(responseSettings);
            this.selectedDepartment = responseSettings.data.departement;
            this.selectedInitialOfferType =
              responseSettings.data.initialOfferType;
            InteraktiveGrafik.getSpecialistTopicImages({
              dept: this.selectedDepartment,
            }).then((responseImage) => {
              this.fillImages(responseImage);
              this.initialDataLoaded = true;
            });
          }
        );
      });
    },
    updateCurrentTemplate(response) {
      console.log("updateCurrentTemplate");
      this.currentTemplate = response.data.template;
      this.currentTemplate.lineTypeName = this.getLineTypeName(
        response.data.template.lineTypeId
      );
      this.currentTemplateId = this.currentTemplate.id;
    },
    async setCurrentTemplate(value) {
      console.log("setCurrentTemplate");
      const template = { templateId: value.id };
      const queryData = { ...template, ...this.glassItemData };
      let response = await InteraktiveGrafik.setCurrentTemplate(queryData);
      if (response.data) {
        this.currentTemplate = value;
        this.currentTemplate.lineTypeName = this.getLineTypeName(
          value.lineTypeId
        );
      }
    },
    getLineTypeName(lineTypeId) {
      if (lineTypeId && this.availableLineTypes.length) {
        const lineType = this.availableLineTypes.find(
          (x) => x.id == lineTypeId
        );
        if (lineType) return lineType.name;
        return "Not found";
      }
      return "Not found";
    },
    fillAvailableLineTypes(response) {
      this.availableLineTypes = response.data;
    },
    fillAvailableOfferTypes(response) {
      this.availableOfferTypes = response.data;
    },
    fillAvailableTemplates(response) {
      this.availableTemplates = response.data;
    },
    fillAvailableSpecialistTopics(response) {
      this.settings.availableSpecialistTopics = response.data;
    },
    fillImages(response) {
      this.availableSpecialistTopicIcons = response.data;
    },
    newSpecialistTopicStart() {
      this.selectedSpecialistTopic = {
        id: "",
        name: "",
        description: "",
        url: "",
        linkText: "",
        icon: { id: "", source: "", alt: "" },
      };
      this.selectedSpecialistTopic.linkText =
        this.translationHelper.getTranslation(
          "ig_fachthema_linktext_defaultvalue"
        );

      var length = this.settings.availableSpecialistTopics.length;
      if (length > 0) {
        this.selectedSpecialistTopic.sortOrder =
          this.settings.availableSpecialistTopics[length - 1].sortOrder + 1;
      } else {
        this.selectedSpecialistTopic.sortOrder = 0;
      }
      this.newSpecialistTopicOpen = true;
    },
    newSpecialistTopicEnd() {
      this.newSpecialistTopicOpen = false;
    },
    specialistTopicSaved(data) {
      this.settings.availableSpecialistTopics.push(data.specialistTopic);
      this.newSpecialistTopicEnd();
    },
    specialistTopicDeleted(specialistTopicId) {
      let index = this.settings.availableSpecialistTopics.findIndex(
        (x) => x.id == specialistTopicId
      );
      if (index >= 0) {
        this.settings.availableSpecialistTopics.splice(index, 1);
      }
    },
    updateSpecialistTopic(selectedSpecialistTopic) {
      let queryData = {
        ...this.glassItemData,
        ["topic"]: selectedSpecialistTopic,
      };
      return InteraktiveGrafik.updateSpecialistTopic(queryData);
    },
    async saveDept() {
      let queryData = {
        ...this.glassItemData,
        ["dept"]: this.selectedDepartment,
      };
      await InteraktiveGrafik.setCurrentDept(queryData);
      var response = await InteraktiveGrafik.getSpecialistTopicImages({
        dept: this.selectedDepartment,
      });
      this.fillImages(response);
    },
    async saveInitialOffer() {
      let queryData = {
        ...this.glassItemData,
        ["offerTypeId"]: this.selectedInitialOfferType,
      };
      await InteraktiveGrafik.setInitialOfferType(queryData);
    }
  }
};
</script>
